import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Building2, CheckCircle2 } from "lucide-react";
import { ogImageMeta } from "@/lib/og-image";
import { trackApplyClick } from "@/lib/apply-cta";

const URL_SELF = "https://vendingmachinefree.com/free-office-vending-machine-placement";
const TITLE = "Free Office Vending Machine Placement — 20+ Employee Offices | $0 Install";
const DESC =
  "Free vending machine placement for corporate offices with 20+ employees. $0 install, $0 service, no contracts. Snack, drink, combo, smart fridge & micro-market options for US offices.";

const FAQS = [
  {
    q: "How many employees do I need to qualify for free office vending?",
    a: "Roughly 20+ on-site staff for a smart fridge; 40+ for a traditional snack/drink machine; 75+ for a full micro-market. Shared multi-tenant buildings qualify at lower per-tenant headcount because operators size to the total building traffic.",
  },
  {
    q: "Is office vending really free?",
    a: "Yes. Machine, delivery, install, restocking, cleaning, insurance, and repairs are all $0. Our revenue comes from product sales — your business pays nothing.",
  },
  {
    q: "What machine types are available for offices?",
    a: "Snack machines, drink machines, combo snack+drink machines, smart fridges, and full micro-markets. We recommend the right mix based on your headcount, shift pattern, and space during a free site survey.",
  },
  {
    q: "How long is the contract?",
    a: "There's no long-term contract. Our placement is at-will — if the location doesn't work for either side, we rotate or remove without penalty.",
  },
  {
    q: "Do you serve shared / coworking offices?",
    a: "Yes. Shared coworking floors with 40+ combined headcount routinely qualify for shared placements. Total building traffic — not just your team — is what matters.",
  },
];

export const Route = createFileRoute("/free-office-vending-machine-placement")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL_SELF },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      ...ogImageMeta(),
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Free Office Vending Machine Placement",
          serviceType: "Free Office Vending Placement",
          provider: { "@type": "Organization", name: "Vending Machine Free.com" },
          areaServed: { "@type": "Country", name: "United States" },
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          url: URL_SELF,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <Link to="/" hash="contact" onClick={() => trackApplyClick("free_office_vending_header")} className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground">
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="bg-charcoal text-charcoal-foreground py-16">
        <div className="container-x max-w-4xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
            <Building2 className="h-4 w-4" /> Office Placement
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase">
            Free Office Vending Machine Placement — 20+ Employee Offices
          </h1>
          <p className="mt-4 text-white/80 text-lg">
            Corporate offices, tech HQs, and shared multi-tenant buildings — snack machines, drink machines,
            combos, smart fridges, and full micro-markets placed at $0 cost. No install fee, no service fee, no contract.
          </p>
          <Link
            to="/"
            hash="contact"
            onClick={() => trackApplyClick("free_office_vending_hero")}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground"
          >
            Apply for Free Placement <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section className="py-14">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-2xl uppercase">What's Included at $0</h2>
          <ul className="mt-6 grid sm:grid-cols-2 gap-3">
            {[
              "Machine or micro-market supply",
              "Delivery & installation",
              "Weekly restocking & rotation",
              "Cash & card collection",
              "Cleaning & maintenance",
              "All repairs & parts",
              "General-liability insurance",
              "24-hour operator response",
            ].map((t) => (
              <li key={t} className="flex gap-2 items-start rounded-md border border-border bg-card p-3 text-sm">
                <CheckCircle2 className="h-5 w-5 text-safety flex-shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-14 bg-secondary">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-2xl uppercase">Best Placement for Your Office Size</h2>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">20–40 staff</div>
              <div className="mt-1 font-display text-lg uppercase">Smart Fridge</div>
              <p className="mt-2 text-sm text-muted-foreground">Compact, tap-to-pay cooler. Ideal for startup floors and small tech offices.</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">40–100 staff</div>
              <div className="mt-1 font-display text-lg uppercase">Combo Vending</div>
              <p className="mt-2 text-sm text-muted-foreground">Snacks + drinks in one unit. Modern touchscreen, tap-to-pay. Fits most breakrooms.</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">100+ staff / shared building</div>
              <div className="mt-1 font-display text-lg uppercase">Micro-Market</div>
              <p className="mt-2 text-sm text-muted-foreground">Full self-checkout mini store — fresh food, premium coffee, 200+ SKUs.</p>
            </div>
          </div>
          <p className="mt-6 text-sm">
            Not sure which fits? <Link to="/free-micro-market-placement" className="text-safety underline">Compare micro-market vs smart fridge</Link> or apply and we'll recommend during your free site survey.
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-3xl uppercase">Office Vending FAQs</h2>
          <div className="mt-8 divide-y divide-border border border-border rounded-xl bg-card">
            {FAQS.map(({ q, a }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer items-center justify-between gap-4 font-semibold list-none">
                  <span>{q}</span>
                  <span className="h-7 w-7 rounded-md bg-secondary flex items-center justify-center text-safety group-open:rotate-45 transition">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-charcoal text-white text-center">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase">Apply for Your Free Office Placement</h2>
          <p className="mt-3 opacity-80">Local operator match within 24 hours.</p>
          <Link
            to="/"
            hash="contact"
            onClick={() => trackApplyClick("free_office_vending_footer")}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
